import React, { useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { X } from "lucide-react";

// Components
import { AppProvider, useAppContext } from "./context";
import Header from "./components/header";
import PushSubscribePrompt from "./components/PushSubscribePrompt";

import Dashboard from "./components/admin-components/pages/dashboard";
import Products from "./components/admin-components/pages/products";
import Orders from "./components/admin-components/pages/orders";
import { AdminProvider } from "./components/admin-components/admin-context";
import AdminLayout from "./components/admin-components/admin-layout";
import Categories from "./components/admin-components/pages/categories";
import UserRoles from "./components/admin-components/pages/user-roles";
import SettingsPage from "./components/admin-components/pages/settings";
import OrderViewPage from "./components/admin-components/pages/order-view";

import HomePage from "./components/customer/pages/HomePage";
import Menu from "./components/customer/pages/Menu";
import About from "./components/customer/pages/About";
import { Blog } from "./components/customer/pages/Blog";
import { Contact } from "./components/customer/pages/Contact";
import { Wishlist } from "./components/customer/pages/WishList";
import Privacy from "./components/customer/pages/Privacy";
import Terms from "./components/customer/pages/Terms";
import Refund from "./components/customer/pages/Refund";
import Shipping from "./components/customer/pages/Shipping";
import { Faq } from "./components/customer/pages/Faq";
import { Profile } from "./components/customer/pages/Profile";
import {
  LoginPage,
  RegisterPage,
} from "./components/customer/components/Login";
import { BlogDetail } from "./components/customer/pages/BlogDetail";
import { CartPageInfo } from "./components/customer/pages/CartPageInfo";
import { Chatbot } from "./components/customer/components/ChatBot";
import { CartDrawer } from "./components/customer/components/CartDrawer";
import { Checkout } from "./components/customer/pages/Checkout";
import DynamicPages, { PublicCustomPage } from "./components/admin-components/pages/DynamicPages";
import Coupons from "./components/admin-components/pages/coupons";
import Customers from "./components/admin-components/pages/customers";
import Reports from "./components/admin-components/pages/reports";
import BlogAdmin from "./components/admin-components/pages/blog";
import Reviews from "./components/admin-components/pages/reviews";
import Banners from "./components/admin-components/pages/banners";
import MediaLibraryPage from "./components/admin-components/pages/media";
import ContactsPage from "./components/admin-components/pages/contacts";
import TicketsPage from "./components/admin-components/pages/tickets";
import NotificationsPage from "./components/admin-components/pages/notifications";
import ReservationsPage from "./components/admin-components/pages/reservations";
import Reservations from "./components/customer/pages/Reservations";
import { Support } from "./components/customer/pages/Support";
import InventoryPage from "./components/admin-components/pages/inventory";
import GiftCardsPage from "./components/admin-components/pages/giftcards";
import LoyaltyAdminPage from "./components/admin-components/pages/loyalty-admin";
import DeliveryZonesPage from "./components/admin-components/pages/deliveryzones";
import OrderTracking from "./components/customer/pages/OrderTracking";
import LoyaltyPage from "./components/customer/pages/LoyaltyPage";
import StaffPage from "./components/admin-components/pages/staff";
import SubscriptionsAdminPage from "./components/admin-components/pages/subscriptions-admin";
import TaxRulesPage from "./components/admin-components/pages/taxrules";
import AuditLogPage from "./components/admin-components/pages/audit-log";
import SocialMediaPage from "./components/admin-components/pages/social-media";
import AffiliatesPage from "./components/admin-components/pages/affiliates";
import MenuBuilderPage from "./components/admin-components/pages/menu-builder";
import POSPage from "./components/admin-components/pages/pos";
import KitchenDisplayPage from "./components/admin-components/pages/kitchen";
import EmailCampaignsPage from "./components/admin-components/pages/email-campaigns";
import SMSCampaignsPage from "./components/admin-components/pages/sms-campaigns";
import TranslationsPage from "./components/admin-components/pages/translations";
import GiftCardsCustomerPage from "./components/customer/pages/GiftCardsPage";
import SubscriptionsPage from "./components/customer/pages/SubscriptionsPage";
import ProductDetail from "./components/customer/pages/ProductDetail";
import CategoryPage from "./components/customer/pages/CategoryPage";
import SearchPage from "./components/customer/pages/SearchPage";
import OrderSuccess from "./components/customer/pages/OrderSuccess";
import ReferralPage from "./components/customer/pages/ReferralPage";
import CustomerNotificationsPage from "./components/customer/pages/CustomerNotificationsPage";
import NotFound from "./components/customer/pages/NotFound";
import StoresPage from "./components/customer/pages/StoresPage";
import Addresses from "./components/customer/pages/Addresses";
import ComparePage from "./components/customer/pages/ComparePage";
import PushNotificationsPage from "./components/admin-components/pages/push-notifications";
import TablesPage from "./components/admin-components/pages/tables";
import ReturnsPage from "./components/admin-components/pages/returns";
import VendorsPage from "./components/admin-components/pages/vendors";
import EventsAdminPage from "./components/admin-components/pages/events-admin";
import EventsPage from "./components/customer/pages/EventsPage";
import CareersPage from "./components/customer/pages/CareersPage";
import CareersAdminPage from "./components/admin-components/pages/careers";
import NavLinksPage from "./components/admin-components/pages/nav-links";

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [pathname]);
  return null;
};

// Reusable Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 transition-opacity">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-white">
          <h2 className="text-xl font-extrabold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin">
            {children}
        </div>
      </div>
    </div>
  );
};

// Component to handle Modal Logic specifically
const ModalManager = () => {
    const { isCartOpen, setIsCartOpen, isPaymentOpen, setIsPaymentOpen } = useAppContext();
    
    return (
        <>
            <Modal isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} title="My Cart">
                <CartDrawer />
            </Modal>
        </>
    );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAppContext();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate replace state={{ from: location }} to="/login" />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { isAdminAuthenticated, isAdmin } = useAppContext();
  const location = useLocation();

  if (!isAdminAuthenticated || !isAdmin) {
    return <Navigate replace state={{ from: location }} to="/admin" />;
  }

  return children;
};

const AdminEntry = () => {
  const { isAdminAuthenticated, isAdmin } = useAppContext();
  const location = useLocation();

  if (!isAdminAuthenticated || !isAdmin) {
    return <LoginPage mode="admin" redirectPath={location.state?.from?.pathname || "/admin"} />;
  }

  return (
    <AdminProvider>
      <AdminLayout />
    </AdminProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppProvider>
          <div className="min-h-screen  bg-gray-50 text-gray-800 font-sans selection:bg-orange-100 selection:text-orange-600">
            <ScrollToTop />
            <Header />
            <PushSubscribePrompt />
            
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/shipping" element={<Shipping />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/support" element={<Support />} />
              <Route path="/cart" element={<CartPageInfo />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/reservations" element={<Reservations />} />
              <Route path="/track" element={<OrderTracking />} />
              <Route path="/track/:orderId" element={<OrderTracking />} />
              <Route path="/loyalty" element={<LoyaltyPage />} />
              <Route path="/gift-cards" element={<GiftCardsCustomerPage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/menu/:id" element={<ProductDetail />} />
              <Route path="/category/:id" element={<CategoryPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/order-success/:id" element={<OrderSuccess />} />
              <Route path="/referral" element={<ReferralPage />} />
              <Route path="/notifications" element={<CustomerNotificationsPage />} />
              <Route path="/stores" element={<StoresPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route
                path="/account/addresses"
                element={
                  <ProtectedRoute>
                    <Addresses />
                  </ProtectedRoute>
                }
              />

              {/* Dynamic custom pages created in the admin */}
              <Route path="/:slug" element={<PublicCustomPage />} />

              {/* 404 fallback */}
              <Route path="*" element={<NotFound />} />

              {/* <Route path="/admin" element={< AdminPanel/>}/> */}
              <Route path="/admin" element={<AdminEntry />}>
                <Route index element={<Dashboard />} />
                <Route
                  path="products"
                  element={
                    <AdminRoute>
                      <Products />
                    </AdminRoute>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <AdminRoute>
                      <Orders />
                    </AdminRoute>
                  }
                />
                <Route
                  path="orders/:orderId"
                  element={
                    <AdminRoute>
                      <OrderViewPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="customers"
                  element={
                    <AdminRoute>
                      <Customers />
                    </AdminRoute>
                  }
                />
                 <Route
                  path="pages"
                  element={
                    <AdminRoute>
                      <DynamicPages />
                    </AdminRoute>
                  }
                />
                <Route path="coupons" element={<Coupons />} /> 
                <Route
                  path="roles"
                  element={
                    <AdminRoute>
                      <UserRoles />
                    </AdminRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <AdminRoute>
                      <SettingsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="categories"
                  element={
                    <AdminRoute>
                      <Categories />
                    </AdminRoute>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <AdminRoute>
                      <Reports />
                    </AdminRoute>
                  }
                />
                <Route
                  path="blog"
                  element={
                    <AdminRoute>
                      <BlogAdmin />
                    </AdminRoute>
                  }
                />
                <Route
                  path="media"
                  element={
                    <AdminRoute>
                      <MediaLibraryPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="contacts"
                  element={
                    <AdminRoute>
                      <ContactsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="tickets"
                  element={
                    <AdminRoute>
                      <TicketsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="notifications"
                  element={
                    <AdminRoute>
                      <NotificationsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="reservations"
                  element={
                    <AdminRoute>
                      <ReservationsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="inventory"
                  element={
                    <AdminRoute>
                      <InventoryPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="gift-cards"
                  element={
                    <AdminRoute>
                      <GiftCardsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="loyalty"
                  element={
                    <AdminRoute>
                      <LoyaltyAdminPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="delivery-zones"
                  element={
                    <AdminRoute>
                      <DeliveryZonesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="staff"
                  element={
                    <AdminRoute>
                      <StaffPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="subscriptions"
                  element={
                    <AdminRoute>
                      <SubscriptionsAdminPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="tax-rules"
                  element={
                    <AdminRoute>
                      <TaxRulesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="reviews"
                  element={
                    <AdminRoute>
                      <Reviews />
                    </AdminRoute>
                  }
                />
                <Route
                  path="banners"
                  element={
                    <AdminRoute>
                      <Banners />
                    </AdminRoute>
                  }
                />
                <Route
                  path="audit-log"
                  element={
                    <AdminRoute>
                      <AuditLogPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="social-media"
                  element={
                    <AdminRoute>
                      <SocialMediaPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="affiliates"
                  element={
                    <AdminRoute>
                      <AffiliatesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="menu-builder"
                  element={
                    <AdminRoute>
                      <MenuBuilderPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="translations"
                  element={
                    <AdminRoute>
                      <TranslationsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="pos"
                  element={
                    <AdminRoute>
                      <POSPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="kitchen"
                  element={
                    <AdminRoute>
                      <KitchenDisplayPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="emails"
                  element={
                    <AdminRoute>
                      <EmailCampaignsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="sms"
                  element={
                    <AdminRoute>
                      <SMSCampaignsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="push-notifications"
                  element={
                    <AdminRoute>
                      <PushNotificationsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="tables"
                  element={
                    <AdminRoute>
                      <TablesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="returns"
                  element={
                    <AdminRoute>
                      <ReturnsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="vendors"
                  element={
                    <AdminRoute>
                      <VendorsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="events"
                  element={
                    <AdminRoute>
                      <EventsAdminPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="careers"
                  element={
                    <AdminRoute>
                      <CareersAdminPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="nav-links"
                  element={
                    <AdminRoute>
                      <NavLinksPage />
                    </AdminRoute>
                  }
                />
            </Route>
            </Routes>

            {/* Global Modals */}

            <ModalManager /> 
            <CartDrawer   /> 
            <Chatbot /> 
          </div>
        </AppProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
